import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import QuotationFlow from "@/pages/QuotationFlow";
import Admin from "@/pages/Admin";
import History from "@/pages/History";
import PublicQuotation from "@/pages/PublicQuotation";
import Landing from "@/pages/Landing";
import Dashboard from "@/pages/Dashboard";
import Team from "@/pages/Team";
import { BrandingProvider } from "@/lib/branding";
import { AuthProvider } from "@/lib/auth";
import AuthCallback from "@/components/AuthCallback";
import ProtectedRoute from "@/components/ProtectedRoute";

const TOAST_OPTIONS = {
  style: {
    background: "#161922",
    border: "1px solid rgba(197,168,128,0.3)",
    color: "#F8F9FA",
  },
};

function AppShell() {
  const location = useLocation();

  // Only the landing page ("/") is indexable. Every app route and the
  // customer quote pages get noindex,nofollow so search engines don't
  // surface secured pages or customer PII.
  useEffect(() => {
    const isLanding = location.pathname === "/";
    let tag = document.querySelector('meta[name="robots"]');
    if (!tag) {
      tag = document.createElement("meta");
      tag.setAttribute("name", "robots");
      document.head.appendChild(tag);
    }
    tag.setAttribute("content", isLanding ? "index,follow" : "noindex,nofollow,noarchive");

    // Canonical: absolute root URL on the landing page only; removed on the
    // noindex app/quote routes so they can't claim a canonical.
    let canonical = document.querySelector('link[rel="canonical"]');
    if (isLanding) {
      if (!canonical) {
        canonical = document.createElement("link");
        canonical.setAttribute("rel", "canonical");
        document.head.appendChild(canonical);
      }
      canonical.setAttribute("href", `${window.location.origin}/`);
    } else if (canonical) {
      canonical.remove();
    }
  }, [location.pathname]);

  // Handle the OAuth callback BEFORE any route/auth logic runs.
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/q/:id" element={<PublicQuotation />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/new"
        element={
          <ProtectedRoute>
            <QuotationFlow />
          </ProtectedRoute>
        }
      />
      <Route
        path="/quotations/:id"
        element={
          <ProtectedRoute>
            <QuotationFlow viewOnlyId />
          </ProtectedRoute>
        }
      />
      <Route
        path="/history"
        element={
          <ProtectedRoute>
            <History />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <ProtectedRoute minRole="manager">
            <Admin />
          </ProtectedRoute>
        }
      />
      <Route
        path="/team"
        element={
          <ProtectedRoute minRole="admin">
            <Team />
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}

function App() {
  return (
    <div className="App app-bg">
      <div className="relative z-10">
        <BrowserRouter>
          <AuthProvider>
            <BrandingProvider>
              <AppShell />
            </BrandingProvider>
          </AuthProvider>
        </BrowserRouter>
      </div>
      <Toaster position="top-center" theme="dark" toastOptions={TOAST_OPTIONS} />
    </div>
  );
}

export default App;
